// BonBoZ: định danh máy này, gửi kèm đăng ký push và báo trạng thái.
//
// `device_id` giúp server nhận ra cùng một máy khi endpoint đổi (đăng ký lại,
// cài lại app). Xoá icon PWA là mất localStorage, nên sẽ thành máy mới.
import { detectOS, OS } from 'shared/helpers/platform';

const DEVICE_ID_KEY = 'bonboz_device_id';

let cachedDeviceId = null;

// `randomUUID` chỉ có trên HTTPS; mã này không cần bảo mật nên có dự phòng.
const generateDeviceId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

// Không đọc/ghi được localStorage (cửa sổ riêng tư) thì giữ mã trong bộ nhớ
// cho hết phiên.
export const getDeviceId = () => {
  if (cachedDeviceId) return cachedDeviceId;
  try {
    cachedDeviceId = localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    cachedDeviceId = null;
  }
  if (cachedDeviceId) return cachedDeviceId;

  cachedDeviceId = generateDeviceId();
  try {
    localStorage.setItem(DEVICE_ID_KEY, cachedDeviceId);
  } catch {
    // Lần mở sau sẽ là mã mới, chấp nhận được.
  }
  return cachedDeviceId;
};

// iPad và máy tính bảng Android cũng tính là 'phone': đều là máy mang theo
// người, khác với laptop.
export const getDeviceType = () => {
  const os = detectOS();
  const isMobileUA = /Mobile/i.test(navigator.userAgent || '');
  return os === OS.IOS || os === OS.ANDROID || isMobileUA ? 'phone' : 'desktop';
};

// Điện thoại/máy tính bảng Android (app cài hay tab Chrome đều tính).
export const isAndroidDevice = () => detectOS() === OS.ANDROID;

const WINDOWS_NT_VERSIONS = new Map([
  ['10.0', '10'],
  ['6.3', '8.1'],
  ['6.2', '8'],
  ['6.1', '7'],
]);

const normalizeVersion = version =>
  version.replace(/_/g, '.').replace(/(\.0)+$/, '');

// Đọc phiên bản hệ điều hành từ User-Agent; không nhận ra thì trả ''.
// Lưu ý: iOS 26 giữ chuỗi "OS 18_6" trong UA, chỉ Safari có thêm "Version/26";
// Chrome Android rút gọn UA thành "Android 10" (xem `getOsVersion`).
export const parseOsVersion = (userAgent = '') => {
  const ios = userAgent.match(/OS (\d+(?:[_.]\d+)*) like Mac OS X/);
  if (ios) {
    const safari = userAgent.match(/Version\/(\d+(?:\.\d+)*)/);
    return `iOS ${normalizeVersion(safari ? safari[1] : ios[1])}`;
  }

  const android = userAgent.match(/Android (\d+(?:\.\d+)*)/);
  if (android) return `Android ${normalizeVersion(android[1])}`;

  const windows = userAgent.match(/Windows NT (\d+\.\d+)/);
  if (windows) {
    const version = WINDOWS_NT_VERSIONS.get(windows[1]);
    return version ? `Windows ${version}` : '';
  }

  const mac = userAgent.match(/Mac OS X (\d+(?:[_.]\d+)*)/);
  if (mac) return `macOS ${normalizeVersion(mac[1])}`;

  return '';
};

// Chrome (Android, Windows, macOS) giấu phiên bản thật khỏi UA, nhưng trả qua
// Client Hints. Trình duyệt không có thì dùng UA.
const versionFromClientHints = async () => {
  const uaData = navigator.userAgentData;
  if (!uaData?.getHighEntropyValues) return '';

  const { platform, platformVersion } = await uaData.getHighEntropyValues([
    'platformVersion',
  ]);
  if (!platformVersion) return '';
  const version = normalizeVersion(platformVersion);
  const major = parseInt(platformVersion, 10);

  if (platform === 'Android') return `Android ${version}`;
  if (platform === 'macOS') return `macOS ${version}`;
  // Theo Microsoft: major >= 13 là Windows 11, 1–10 là Windows 10.
  if (platform === 'Windows' && major >= 13) return 'Windows 11';
  if (platform === 'Windows' && major >= 1) return 'Windows 10';
  return '';
};

export const getOsVersion = async () => {
  try {
    const hinted = await versionFromClientHints();
    if (hinted) return hinted;
  } catch {
    // Không đọc được Client Hints thì dùng UA.
  }
  return parseOsVersion(navigator.userAgent || '');
};

// Ba trường gửi kèm mọi request về thông báo đẩy của máy này.
export const getDeviceInfo = async () => ({
  device_id: getDeviceId(),
  device_type: getDeviceType(),
  os_version: await getOsVersion(),
});
